<script lang="ts">
    import MeetingManagerPage from '@Evaluation/Estimations/Pages/MeetingManagerPage'
    export default MeetingManagerPage
</script>

<template>
    <div>
        <MeetingStarter :viewModel="meetingStarter"
                        @startMeeting="StartMeeting"
                        @openSurvey="handleOpenPanelAccessCommand"
                        @closeSurvey="handleClosePanelAccessCommand"
                        @toggleAutoReveal="ToggleAutoRevealCommand"
                        @toggleNamesRevealed="ToggleNamesRevealedCommand"
                        @showAccessLinks="ShowAccessLinksCommand" />

        <MeetingStopper :viewModel="meetingStopper"
                        @stopMeeting="StopMeeting"
                        @openSurvey="handleOpenPanelAccessCommand"
                        @closeSurvey="handleClosePanelAccessCommand" />
        
        <PanelOpener :viewModel="panelOpener" 
                     @open-panel="handleOpenPanelAccessCommand" 
                     @show-links="ShowAccessLinksCommand" />
        
        <PanelCloser :viewModel="panelCloser" 
                     @close-access="handleClosePanelAccessCommand" />
        
        <SurveyAccessLinks :viewModel="surveyAccessLinks" 
                           @send-email="handleSendEmailCommand"
                           @download-links="downloadLinks" />
        
        <AttributeDescriptionDialog :viewModel="attributeDescriptionDialog" />
        
        <AlternativeDetailsDialog :viewModel="alternativeDetailsDialog" />

        <DisagreementsDialog :viewModel="disagreementsDialog" @switch-question="SwitchQuestion" />

        <PortfolioOrDecisionNavigation :workspaceId="workspaceId" :projectId="projectId" />

        <h3 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading... </h3>

        <div v-if="initialized && questionsCount === 0" class="d-flex justify-content-center" style="padding-top: 200px;">
            <div class="card">
                <div class="card-body">
                    <h5 class="card-title">No questions</h5>
                    <p class="card-text lead">Sorry. This panel has no questions.</p>
                    <a class="btn btn-secondary" v-sanitizedHref="BackUrl">Go back</a>
                </div>
            </div>
        </div>

        <div v-if="initialized && questionsCount > 0">

            <nav class="navbar fixed-bottom navbar-dark bg-dark d-flex justify-content-center">
                <div class="btn-group" role="group">

                    <button @click="PreviousQuestionCommand" type="button" class="btn btn-secondary">
                        <i class="fa fa-chevron-left" aria-hidden="true"></i> <span>Prev question</span>
                    </button>

                    <button v-if="!AllQuestionsAnswered" @click="NextUnansweredQuestionCommand" type="button" class="btn btn-warning" title="Go to next unanswered question">
                        Next unanswered
                    </button>

                    <button v-if="AllQuestionsAnswered" @click="AllDoneCommand" type="button" class="btn btn-success" title="Go to next unanswered question">
                        All done
                    </button>

                    <button @click="NextQuestionCommand" type="button" class="btn btn-secondary">
                        <span>Next question</span> <i class="fa fa-chevron-right" aria-hidden="true"></i>
                    </button>
                </div>
            </nav>

            <div class="container-fluid" style="padding-top: 130px;">
                <div class="row">
                    <div class="col-6">
                        <h6 class="mx-5">
                            <a class="text-dark" v-sanitizedHref="BackUrl">
                                <i class="fa fa-angle-double-left" aria-hidden="true"></i> Back
                            </a>
                        </h6>
                        <h1 class="my-0 mx-5" style="font-size: 2em; font-weight: 300; color: #33475b;">Panel: {{Name}}</h1>
                    </div>

                    <div class="col-6 col-lg-5 text-right col align-self-end">

                        <div v-if="!MeetingStarted">
                            <button type="button" class="btn btn-success mr-1" @click="ShowMeetingStarterCommand">Start Meeting</button>

                            <button v-if="!SurveyStarted" type="button" class="btn btn-outline-secondary mr-1" @click="ShowSurveyOpenerCommand">Open Survey</button>
                            <button v-if="SurveyStarted" type="button" class="btn btn-outline-secondary mr-1" @click="ShowSurveyCloserCommand">Close Survey</button>

                            <button type="button" class="btn btn-outline-secondary mr-1" @click="ShowAccessLinksCommand">Access links</button>

                            <div class="btn-group">
                                <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                    Analyze votes
                                </button>
                                <div class="dropdown-menu dropdown-menu-right">
                                    <a class="dropdown-item" @click="ShowDisagreementsReportCommand">Disagreements</a>
                                </div>
                            </div>
                        </div>

                        <div v-if="MeetingStarted">

                            <div class="btn-group mr-1">
                                <button type="button" class="btn btn-warning" @click="ToggleVotesRevealedCommand">
                                    <i class="fa-regular fa-fw" :class="VotesRevealed ? 'fa-square-check' : 'fa-square'"></i> Reveal votes
                                </button>
                                <button type="button" class="btn btn-warning dropdown-toggle dropdown-toggle-split" data-toggle="dropdown"></button>
                                <div class="dropdown-menu dropdown-menu-right">
                                    <a class="dropdown-item px-1" @click.stop.prevent="ToggleAutoRevealCommand">
                                        <i class="fa-regular fa-fw" :class="AutoRevealEnabled ? 'fa-square-check' : 'fa-square'"></i> Auto reveal
                                    </a>
                                    <a class="dropdown-item px-1" @click.stop.prevent="ToggleNamesRevealedCommand">
                                        <i class="fa-regular fa-fw" :class="!NamesRevealed ? 'fa-square-check' : 'fa-square'"></i> Anonymous
                                    </a>
                                </div>
                            </div>

                            <div class="btn-group mr-1">
                                <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                    Analyze votes
                                </button>
                                <div class="dropdown-menu dropdown-menu-right">
                                    <a class="dropdown-item" @click="ShowDisagreementsReportCommand">Disagreements</a>
                                </div>
                            </div>

                            <div class="btn-group">
                                <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                    Actions
                                </button>
                                <div class="dropdown-menu dropdown-menu-right">
                                    <a class="dropdown-item" @click="ShowMeetingStopperCommand">Stop Meeting</a>
                                    <div class="dropdown-divider"></div>
                                    <a v-if="!SurveyStarted" class="dropdown-item" @click="ShowSurveyOpenerCommand">Open Survey</a>
                                    <a v-if="SurveyStarted" class="dropdown-item" @click="ShowSurveyCloserCommand">Close Survey</a>
                                    <a class="dropdown-item" @click="ShowAccessLinksCommand">Access links</a>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                <hr style="border-color: #bbb; margin: 20px 0 60px;" />
                <div class="row">
                    <div class="col-lg-3 col-md-3 col-sm-4 col-xs-12" style="padding-bottom: 100px;">
                        <div class="card">
                            <div class="card-header">
                                Questions <strong>{{ProgressDescription}}</strong>
                                <div class="float-right">
                                    <a class="text-dark" @click="SwitchQuestionsOrderCommand">{{QuestionsOrderLabel}}</a>
                                </div>
                            </div>
                            <div class="card-body">

                                <div v-if="QuestionsOrder == 1">
                                    <div class="dropdown">
                                        <a class="h5 dropdown-toggle" data-toggle="dropdown" style="white-space: normal;">
                                            <span>{{CurrentAttributeLabel}}</span>
                                        </a>
                                        <div class="dropdown-menu" style="max-height: 300px; overflow-y:auto;">
                                            <div class="input-group px-3 mb-1">
                                                <input type="search" placeholder="Search..." class="form-control" v-model="SearchPhrase">
                                                <span class="input-group-append">
                                                    <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
                                                </span>
                                            </div>
                                            <a v-for="attr in AttributesView" class="dropdown-item" @click="SwitchToAttributeCommand(attr)">
                                                {{attr.Name}} <br />
                                            </a>
                                        </div>
                                    </div>

                                    <div class="list-group mt-2">
                                        <a v-for="q in QuestionsView" class="list-group-item list-group-item-action" :class="{ 'bg-secondary text-white': CurrentQuestionId === q.QuestionId }" @click="SwitchToQuestionCommand(q)">
                                            <i :class="getStatusCss(q)"></i>
                                            {{q.AlternativeName}}
                                            <small v-if="q.GroupVoteValue != null"><br />Value: {{q.GroupVoteValue}}</small>
                                        </a>
                                    </div>
                                </div>

                                <div v-if="QuestionsOrder == 2">
                                    <div class="dropdown">
                                        <a class="h5 dropdown-toggle" data-toggle="dropdown" style="white-space: normal;">
                                            <span>{{CurrentAlternativeLabel}}</span>
                                        </a>
                                        <div class="dropdown-menu" style="max-height: 300px; overflow-y:auto;">
                                            <div class="input-group px-3 mb-1">
                                                <input type="search" placeholder="Search..." class="form-control" v-model="SearchPhrase">
                                                <span class="input-group-append">
                                                    <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
                                                </span>
                                            </div>

                                            <a v-for="a in AlternativesView" class="dropdown-item" @click="SwitchToAlternativeCommand(a)">
                                                {{a.Name}}<br />
                                            </a>

                                        </div>

                                        <div class="list-group mt-2">
                                            <a v-for="q in QuestionsView" class="list-group-item list-group-item-action" :class="{ 'bg-secondary text-white': CurrentQuestionId === q.QuestionId }" @click="SwitchToQuestionCommand(q)">
                                                <i :class="getStatusCss(q)"></i>
                                                {{q.AttributeName}}
                                                <small v-if="q.GroupVoteValue != null"><br />Value: {{q.GroupVoteValue}}</small>
                                            </a>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    <div class="col-lg-7 offset-lg-1 col-md-9 col-sm-8 col-xs-12" style="padding-bottom: 100px;">

                        <div v-if="Question">
                            <p class="text-center lead" style="font-size: 1.5rem;">
                                How would you rate: <a class="text-primary" title="Show description" @click="ShowAlternativeDetailsCommand(Question.AlternativeId)">{{Question.AlternativeName}}</a><br />
                                in the context of: <a class="text-primary" title="Show description" @click="ShowAttributeDetailsCommand(Question.AttributeId)">{{Question.AttributeName}}</a>?
                            </p>

                            <div class="card card-collapsable mt-1">
                                <div class="card-header pointer" data-toggle="collapse" href="#cardQuestion">
                                    Group vote
                                </div>
                                <div id="cardQuestion" class="card-body collapse show pt-4">
                                    
                                    <HostVoteEditor :readModel="Question"
                                                    @update-comment="saveComment"
                                                    @update-value="saveValue" />
                                </div>
                            </div>

                            <div class="mt-3">
                                <div class="mt-4" style="min-height: 500px;">
                                    <VotesBrowser :AlternativeName="Question.AlternativeName"
                                                :HasScale="Question.HasScale"
                                                :Entries="Question.Entries"
                                                :ScaleLevels="Question.ScaleLevels" />
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>

    </div>
</template>
